import { useId } from 'react';
import { Alert, Box, Checkbox, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';

type GatewayValue = string | number;

interface GatewayOption { value: GatewayValue; label: string; caption?: string; claimed: boolean; }
interface DelistedGateway { value: GatewayValue; label: string; }

export default function GatewayMultiSelect({ label, value, options, delisted, loading, error, emptyText, onChange }: {
  label: string;
  value: GatewayValue[];
  options: GatewayOption[];
  delisted: DelistedGateway[];
  loading: boolean;
  error: string | null;
  emptyText: string;
  onChange: (values: GatewayValue[]) => void;
}) {
  const labelId = useId();
  const labelByValue = new Map<GatewayValue, string>([
    ...delisted.map((g) => [g.value, g.label] as const),
    ...options.map((g) => [g.value, g.label] as const),
  ]);
  const delistedValues = delisted.map((g) => g.value);

  return (
    <>
      <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={loading || error != null}>
        <InputLabel id={labelId} shrink>{label}</InputLabel>
        <Select labelId={labelId} multiple value={value} displayEmpty
          input={<OutlinedInput notched label={label} />}
          renderValue={(selected) => (selected.length === 0
            ? <Box component="span" sx={{ color: 'text.secondary' }}>No gateways</Box>
            : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => <Chip key={v} size="small" label={labelByValue.get(v) ?? String(v)} />)}
            </Box>)}
          onChange={(e) => onChange(typeof e.target.value === 'string' ? [] : e.target.value)}>
          {delisted.map((g) => (
            <MenuItem key={g.value} value={g.value}>
              <Checkbox size="small" checked />
              <Typography variant="body2">{g.label}</Typography>
            </MenuItem>
          ))}
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} disabled={o.claimed}>
              <Checkbox size="small" checked={value.includes(o.value)} disabled={o.claimed} />
              <Box>
                <Typography variant="body2">{o.label}</Typography>
                {o.caption && <Typography variant="caption" color="text.secondary">{o.caption}</Typography>}
              </Box>
            </MenuItem>
          ))}
          {options.length === 0 && !loading && error == null && <MenuItem disabled>{emptyText}</MenuItem>}
        </Select>
      </FormControl>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {delistedValues.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {delistedValues.join(', ')} {delistedValues.length === 1 ? 'is' : 'are'} no longer in the {label} list for this account.
          Saving keeps {delistedValues.length === 1 ? 'it' : 'them'}.
        </Alert>
      )}
    </>
  );
}
